import { ReactNode } from 'react';
import { FormikProps } from 'formik';
import { Input } from '../../../components/common/Form';
import Button from '../../../components/common/Button';
import InstagramIcon from '../../../assets/svgs/icons/Socials/InstagramIcon';
import FacebookIcon from '../../../assets/svgs/icons/Socials/FacebookIcon';
import SnapchatIcon from '../../../assets/svgs/icons/Socials/SnapchatIcon';
import TiktokIcon from '../../../assets/svgs/icons/Socials/TiktokIcon';
import TwitterIcon from '../../../assets/svgs/icons/Socials/TwitterIcon';
import SocialInputLabel from '../../../components/SocialsInputLabel';
import { handleHttpsInput } from '../../../utils/general';
import { RestaurantWizardFormValues } from './restaurantWizardSchema';

type SocialField = keyof RestaurantWizardFormValues['socials'];
type SocialErrors = Partial<Record<SocialField, string>>;
type SocialTouched = Partial<Record<SocialField, boolean>>;

interface SocialConfig {
  name: SocialField;
  url: string;
  icon: ReactNode;
}

const SOCIAL_INPUTS: SocialConfig[] = [
  { name: 'instagram', url: 'https://instagram.com/username', icon: <InstagramIcon /> },
  { name: 'tiktok', url: 'https://tiktok.com/@username', icon: <TiktokIcon /> },
  { name: 'facebook', url: 'https://facebook.com/username', icon: <FacebookIcon /> },
  { name: 'twitter', url: 'https://twitter.com/username', icon: <TwitterIcon /> },
  { name: 'snapchat', url: 'https://snapchat.com/add/username', icon: <SnapchatIcon /> }
];

interface SocialInputProps extends SocialConfig {
  formik: FormikProps<RestaurantWizardFormValues>;
}

const SocialInput = ({ name, url, icon, formik }: SocialInputProps) => {
  const socialErrors = formik.errors.socials as SocialErrors | undefined;
  const socialTouched = formik.touched.socials as SocialTouched | undefined;

  return (
    <Input
      name={`socials.${name}`}
      label={<SocialInputLabel label={name} icon={icon} />}
      className="restaurant-wizard-socials-input"
      onChange={(event: any) => formik.setFieldValue(`socials.${name}`, handleHttpsInput(event.target.value))}
      onBlur={formik.handleBlur}
      value={formik.values.socials[name]}
      touched={socialTouched?.[name]}
      error={socialErrors?.[name]}
      placeholder={url}
    />
  );
};

interface RestaurantSocialsPageProps {
  formik: FormikProps<RestaurantWizardFormValues>;
  isEdit: boolean;
  onNext: () => void;
  onBack: () => void;
  onSave: () => void;
}

const RestaurantSocialsPage = ({ formik, isEdit, onNext, onBack, onSave }: RestaurantSocialsPageProps) => {
  const validateSocials = async () => {
    formik.setTouched({ socials: Object.fromEntries(SOCIAL_INPUTS.map(({ name }) => [name, true])) }, true);
    const errors = await formik.validateForm();
    const socialErrors = errors.socials as SocialErrors | undefined;
    return SOCIAL_INPUTS.every(({ name }) => !socialErrors?.[name]);
  };

  const handleNextClicked = async () => {
    if (await validateSocials()) {
      onNext();
    }
  };

  const handleSaveClicked = async () => {
    if (await validateSocials()) {
      onSave();
    }
  };

  return (
    <div className={`restaurant-socials-page${isEdit ? ' restaurant-socials-page--edit' : ''}`}>
      <div className="restaurant-wizard-socials-inputs">
        {SOCIAL_INPUTS.map(({ name, url, icon }) => (
          <SocialInput key={name} name={name} url={url} icon={icon} formik={formik} />
        ))}
      </div>
      <div className="restaurant-wizard-socials-buttons">
        {!isEdit && (
          <Button className="restaurant-wizard-back-button" onClick={onBack}>
            BACK
          </Button>
        )}
        {isEdit ? (
          <Button className="restaurant-wizard-save-button" onClick={handleSaveClicked}>
            SAVE
          </Button>
        ) : (
          <Button className="restaurant-wizard-next-button" onClick={handleNextClicked}>
            NEXT
          </Button>
        )}
      </div>
    </div>
  );
};

export default RestaurantSocialsPage;
